/* oxlint-disable import/no-unassigned-import -- Highcharts modules register themselves through side-effect imports. */
/**
 * Central Highcharts configuration.
 * Initializes Highcharts with required modules.
 */
// Import Highstock - must come first
import Highcharts from 'highcharts/highstock';

// imports - these auto-registers with Highcharts
// highcharts-more adds arearange, areasplinerange, columnrange, bubble,
// packedbubble, boxplot, errorbar, waterfall, gauge and polar support
import 'highcharts/highcharts-more';
import 'highcharts/modules/sankey';
// series-type modules — a module must come after the module it extends
// (sankey → dependency-wheel/organization/arc-diagram, treemap → sunburst/treegraph,
// heatmap → tilemap, dumbbell → lollipop)
import 'highcharts/modules/arc-diagram';
import 'highcharts/modules/dependency-wheel';
import 'highcharts/modules/organization';

import 'highcharts/modules/treemap';
import 'highcharts/modules/sunburst';
import 'highcharts/modules/treegraph';
import 'highcharts/modules/heatmap';
import 'highcharts/modules/tilemap';
import 'highcharts/modules/dumbbell';
import 'highcharts/modules/lollipop';
import 'highcharts/modules/bullet';
import 'highcharts/modules/funnel'; // funnel + pyramid
import 'highcharts/modules/heikinashi';
import 'highcharts/modules/histogram-bellcurve';
import 'highcharts/modules/hollowcandlestick';
import 'highcharts/modules/item-series';
import 'highcharts/modules/marker-clusters';
import 'highcharts/modules/networkgraph';
import 'highcharts/modules/parallel-coordinates';
import 'highcharts/modules/pareto';
import 'highcharts/modules/pictorial';
import 'highcharts/modules/solid-gauge';
import 'highcharts/modules/streamgraph';
import 'highcharts/modules/timeline';
import 'highcharts/modules/variable-pie';
import 'highcharts/modules/variwide';
import 'highcharts/modules/vector';
import 'highcharts/modules/venn';
import 'highcharts/modules/windbarb';
import 'highcharts/modules/wordcloud';
import 'highcharts/modules/xrange';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/full-screen';
import 'highcharts/modules/drag-panes';
import 'highcharts/modules/stock-tools';
import 'highcharts/modules/annotations-advanced';
import 'highcharts/modules/price-indicator';
import 'highcharts/themes/adaptive';
import 'highcharts/indicators/indicators-all';
import 'highcharts/indicators/apo';
import 'highcharts/indicators/aroon';
import 'highcharts/indicators/aroon-oscillator';
import 'highcharts/indicators/bollinger-bands';
import 'highcharts/indicators/cmo';
import 'highcharts/indicators/dema';
import 'highcharts/indicators/disparity-index';
import 'highcharts/indicators/dpo';
import 'highcharts/indicators/indicators';
import 'highcharts/indicators/macd';
import 'highcharts/indicators/ppo';
import 'highcharts/indicators/psar';
import 'highcharts/indicators/regressions';
import 'highcharts/indicators/roc';
import 'highcharts/indicators/rsi';
import 'highcharts/indicators/tema';
import 'highcharts/indicators/trendline';
import 'highcharts/indicators/trix';

// Always use client-side (offline) export — the Highcharts export server
Highcharts.setOptions({ exporting: { fallbackToExportServer: false } });

const PRICE_LABEL_GAP_PX = 2;
const PRICE_LABEL_TEXT_Y_OFFSET_PX = 3;
const priceLabelTextBaseY = new WeakMap<Highcharts.SVGElement, number>();

type SeriesWithPriceLabels = Highcharts.Series & {
  lastPrice?: Highcharts.SVGElement;
  lastPriceLabel?: Highcharts.SVGElement;
  lastVisiblePrice?: Highcharts.SVGElement;
  lastVisiblePriceLabel?: Highcharts.SVGElement;
};

type PriceLabel = {
  height: number;
  label: Highcharts.SVGElement;
  y: number;
};

const offsetPriceLabelText = (label: Highcharts.SVGElement): void => {
  const textElement = (label as Highcharts.SVGElement & { text?: Highcharts.SVGElement }).text;
  if (!textElement) return;

  const rawTextY = textElement.attr('y');
  const textY = typeof rawTextY === 'number' ? rawTextY : Number.parseFloat(rawTextY);
  if (!Number.isFinite(textY)) return;

  const previousBaseY = priceLabelTextBaseY.get(textElement);
  const isAlreadyOffset =
    previousBaseY !== undefined &&
    Math.abs(textY - (previousBaseY + PRICE_LABEL_TEXT_Y_OFFSET_PX)) < 0.5;
  const baseY = isAlreadyOffset ? previousBaseY : textY;
  priceLabelTextBaseY.set(textElement, baseY);

  textElement.attr({ y: baseY + PRICE_LABEL_TEXT_Y_OFFSET_PX });
};

const getPriceLabel = (series: SeriesWithPriceLabels): PriceLabel | null => {
  const label = series.lastVisiblePriceLabel ?? series.lastPriceLabel;
  if (!label || !series.visible) return null;

  const rawY = label.attr('y');
  const y = typeof rawY === 'number' ? rawY : Number.parseFloat(rawY);
  if (!Number.isFinite(y)) return null;

  const height = Math.max(1, Math.round(label.getBBox().height));
  return { label, y, height };
};

const hidePriceIndicators = (seriesList: Highcharts.Series[]): void => {
  seriesList.forEach(series => {
    const indicatorSeries = series as SeriesWithPriceLabels;
    indicatorSeries.lastPrice?.hide();
    indicatorSeries.lastVisiblePrice?.hide();
  });
};

const getSortedPriceLabels = (seriesList: Highcharts.Series[]): PriceLabel[] =>
  seriesList
    .map(series => getPriceLabel(series as SeriesWithPriceLabels))
    .filter((item): item is PriceLabel => item !== null)
    .toSorted((left, right) => left.y - right.y);

const spacePriceLabelsFromTop = (labels: PriceLabel[], minY: number): number => {
  let previousBottom = minY;

  for (const item of labels) {
    const adjustedY = Math.max(item.y, previousBottom + PRICE_LABEL_GAP_PX);
    item.y = adjustedY;
    previousBottom = adjustedY + item.height;
  }

  return previousBottom;
};

const shiftPriceLabels = (labels: PriceLabel[], offset: number): void => {
  if (offset === 0) return;

  for (const item of labels) {
    item.y += offset;
  }
};

const fitPriceLabelsInPlot = (labels: PriceLabel[], minY: number, maxY: number): void => {
  if (labels.length <= 1) return;

  const previousBottom = spacePriceLabelsFromTop(labels, minY);
  shiftPriceLabels(labels, -Math.max(0, previousBottom - maxY));
  shiftPriceLabels(labels, Math.max(0, minY - labels[0].y));
};

const applyPriceLabelPositions = (labels: PriceLabel[]): void => {
  labels.forEach(item => {
    item.label.attr({ y: Math.round(item.y) });
    offsetPriceLabelText(item.label);
  });
};

const stackPriceIndicators = (chart: Highcharts.Chart): void => {
  hidePriceIndicators(chart.series);
  const labels = getSortedPriceLabels(chart.series);
  if (labels.length === 0) return;

  fitPriceLabelsInPlot(labels, chart.plotTop, chart.plotTop + chart.plotHeight);
  applyPriceLabelPositions(labels);
};

const removeStackHandler = Highcharts.addEvent(Highcharts.Chart, 'render', function () {
  stackPriceIndicators(this);
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    removeStackHandler();
  });
}

export { Highcharts };
